import { Extension } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";

import { extractResourceTokenInners } from "../../domain/formula-grammar";
import type { ReactionResourceOption } from "../suggestion/suggestion-types";
import { screenFieldBraceInnerMatchesLabel } from "@/components/rule-canvas-builder/utils/data-field-type-utils";
import {
	decodeResourceSlotToken,
	formatResourceSlotsAttr,
	parseResourceSlotsAttr,
} from "./paragraph-resource-meta";

export type ResourceOptionsForPruneRef = { current: ReactionResourceOption[] };

export interface ResourceSlotsPruneOptions {
  /**
   * Lets `screenField` slots (`<id>:::screenField`) stay while `{label}` is present — labels are
   * resolved from this list by matching `resourceType === "screenField"` and `value`.
   */
  resourceOptionsForPrune?: ReactionResourceOption[];
  /** When set, read at prune time so newly inserted picks are visible before the next render. */
  resourceOptionsRef?: ResourceOptionsForPruneRef;
}

function braceInnerSetFromParagraph(paragraph: Node): Set<string> {
	const text = paragraph.textBetween(0, paragraph.content.size, "", "");
	return new Set(extractResourceTokenInners(text));
}

/** Prune `data-resource-slots` against paragraph text (exported for tests). */
export function pruneResourceSlotsForText(
	slotsRaw: string | null,
	paragraphText: string,
	resources: ReactionResourceOption[],
): string | null {
	return pruneParagraphSlots(slotsRaw, new Set(extractResourceTokenInners(paragraphText)), resources);
}

function screenFieldSlotStillPresent(
	fieldId: string,
	braceInners: Set<string>,
	resources: ReactionResourceOption[]
): boolean {
	if (braceInners.has(fieldId)) {
		return true;
	}
	const opt = resources.find(
		(r) => r.resourceType === "screenField" && String(r.value) === fieldId
	);
	if (!opt) {
		return false;
	}
	for (const inner of braceInners) {
		if (screenFieldBraceInnerMatchesLabel(inner, opt.label)) {
			return true;
		}
	}
	return false;
}

function advancedTriggerSlotStillPresent(
	triggerName: string,
	braceInners: Set<string>,
	resources: ReactionResourceOption[]
): boolean {
	if (braceInners.has(triggerName)) {
		return true;
	}
	const opt = resources.find(
		(r) => r.resourceType === "advancedTrigger" && r.triggerName === triggerName
	);
	return Boolean(opt && braceInners.has(opt.label.trim()));
}

function pruneParagraphSlots(
	slotsRaw: string | null,
	braceInners: Set<string>,
	resources: ReactionResourceOption[]
): string | null {
	const slots = parseResourceSlotsAttr(slotsRaw);
	const kept = slots.filter((token) => {
		const decoded = decodeResourceSlotToken(token);
		if (!decoded) {
			return false;
		}
		if (decoded.resourceType === "screenField") {
			const id = decoded.braceInner.trim();
			return screenFieldSlotStillPresent(id, braceInners, resources);
		}
		if (decoded.resourceType === "advancedTrigger") {
			const triggerName = decoded.braceInner.trim();
			return advancedTriggerSlotStillPresent(triggerName, braceInners, resources);
		}
		return braceInners.has(decoded.braceInner.trim());
	});
	return kept.length ? formatResourceSlotsAttr(kept) : null;
}

/** Drops stale `data-resource-slots` entries when `{…}` tokens disappear. */
export const ResourceSlotsPrune = Extension.create<ResourceSlotsPruneOptions>({
	name: "resourceSlotsPrune",

	addOptions() {
		return {
			resourceOptionsForPrune: [] as ReactionResourceOption[],
		};
	},

	addProseMirrorPlugins() {
		const extension = this;

		return [
			new Plugin({
				key: new PluginKey("resourceSlotsPrune"),

				appendTransaction(_transactions, _oldState, newState) {
					const resources =
            extension.options.resourceOptionsRef?.current ??
            extension.options.resourceOptionsForPrune ??
            [];

					let tr = newState.tr;
					let modified = false;

					newState.doc.descendants((node, pos) => {
						if (node.type.name !== "paragraph") {
							return;
						}

						const raw = node.attrs.resourceSlots as string | null;
						if (!raw) {
							return;
						}

						const braceInners = braceInnerSetFromParagraph(node);
						const next = pruneParagraphSlots(raw, braceInners, resources);
						const cur = raw;

						const curNormalized = cur ?? null;
						const nextNormalized = next ?? null;
						if (curNormalized !== nextNormalized) {
							tr = tr.setNodeMarkup(pos, undefined, {
								...node.attrs,
								resourceSlots: next,
							});
							modified = true;
						}
					});

					return modified ? tr : null;
				},
			}),
		];
	},
});
